import React from 'react';
import { Select, Input, Popover, Button, Modal, Tag } from 'antd';
import { BsPlusLg } from 'react-icons/bs';
import { RUN_LIMITS, RUN_SORT_OPTIONS, RUN_TYPE_FILTERS, DAG_RUN_STATE_FILTERS } from '../../constants';
import { RunHistoryTable } from './run-history-table';
import { useRunsTab } from './use-runs-tab';
import styles from '../../styles.module.scss';

const { Option } = Select;

type RunsTabProps = {
    dagId: string;
    refreshKey: number;
};

const CONFIRM_COPY: Record<string, { title: string; description: (runId: string) => string; okColor: string; okGlow: string }> = {
    success: { title: 'Mark Run as Success?', description: (runId) => `Mark run "${runId}" as success?`, okColor: '#08c46a', okGlow: 'rgba(8, 196, 106, 0.72)' },
    failed: { title: 'Mark Run as Failed?', description: (runId) => `Mark run "${runId}" as failed?`, okColor: '#ff4d4f', okGlow: 'rgba(255, 77, 79, 0.72)' },
    delete: { title: 'Delete Run?', description: (runId) => `Are you sure you want to delete run "${runId}"? This cannot be undone.`, okColor: '#ff4d4f', okGlow: 'rgba(255, 77, 79, 0.72)' },
};

/*
 * DAG detail's Runs tab — sort/page-size, quick and advanced filters, run history table, actions
 */
export function RunsTab({ dagId, refreshKey }: RunsTabProps) {
    const {
        runs,
        totalEntries,
        totalPages,
        isLoading,
        sortBy,
        setSortBy,
        pageSize,
        setPageSize,
        page,
        setPage,
        stateFilter,
        runTypeFilter,
        appliedFilters,
        draftFilters,
        activeFilterCount,
        confirmAction,
        setConfirmAction,
        handleStateFilterChange,
        handleRunTypeFilterChange,
        handleOpenAdvancedFilters,
        handleDraftFilterChange,
        handleApplyAdvancedFilters,
        handleClearFilter,
        handleClearAllFilters,
        handleConfirmAction,
        handleRunClick,
    } = useRunsTab(dagId, refreshKey);

    const chips: { key: keyof typeof appliedFilters; label: string }[] = [
        { key: 'logicalDateFrom', label: 'Logical Date' },
        { key: 'runAfterFrom', label: 'Run After' },
        { key: 'startDateFrom', label: 'Start Date' },
        { key: 'endDateFrom', label: 'End Date' },
        { key: 'durationFrom', label: 'Duration' },
        { key: 'runIdPattern', label: 'Run ID' },
        { key: 'triggeringUser', label: 'Triggering User' },
    ];

    return (
        <div className={styles.card}>
            <div className={styles.filterRow}>
                <h3 style={{ margin: 0, color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: 0.25 }}>{`Run History (${totalEntries})`}</h3>
                <div className={styles.filterRowEnd}>
                    <Select value={sortBy} onChange={setSortBy} style={{ width: 200 }}>
                        {RUN_SORT_OPTIONS.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                    <Select
                        value={pageSize}
                        onChange={(value) => {
                            setPageSize(value);
                            setPage(1);
                        }}
                        style={{ width: 90 }}
                    >
                        {RUN_LIMITS.map((limit) => (
                            <Option key={limit} value={limit}>
                                {limit}
                            </Option>
                        ))}
                    </Select>
                </div>
            </div>

            <div className={styles.filterRow}>
                <Select value={stateFilter || undefined} placeholder="State" allowClear style={{ width: 140 }} onChange={(value) => handleStateFilterChange(value ?? '')}>
                    {DAG_RUN_STATE_FILTERS.map((option) => (
                        <Option key={option.value} value={option.value}>
                            {option.label}
                        </Option>
                    ))}
                </Select>
                <Select value={runTypeFilter || undefined} placeholder="Run Type" allowClear style={{ width: 140 }} onChange={(value) => handleRunTypeFilterChange(value ?? '')}>
                    {RUN_TYPE_FILTERS.map((option) => (
                        <Option key={option.value} value={option.value}>
                            {option.label}
                        </Option>
                    ))}
                </Select>

                {chips.map(({ key, label }) =>
                    appliedFilters[key] ? (
                        <Tag key={key} closable onClose={() => handleClearFilter(key)}>
                            {`${label}: ${appliedFilters[key]}`}
                        </Tag>
                    ) : null
                )}

                <Popover
                    overlayClassName="airflowPopoverOverlay"
                    trigger="click"
                    onVisibleChange={(visible) => visible && handleOpenAdvancedFilters()}
                    content={
                        <div style={{ width: 320, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Input placeholder="Run ID Pattern" value={draftFilters.runIdPattern} onChange={(e) => handleDraftFilterChange('runIdPattern', e.target.value)} />
                            <Input placeholder="Triggering User" value={draftFilters.triggeringUser} onChange={(e) => handleDraftFilterChange('triggeringUser', e.target.value)} />
                            {[
                                { fromKey: 'logicalDateFrom' as const, toKey: 'logicalDateTo' as const, label: 'Logical Date' },
                                { fromKey: 'runAfterFrom' as const, toKey: 'runAfterTo' as const, label: 'Run After' },
                                { fromKey: 'startDateFrom' as const, toKey: 'startDateTo' as const, label: 'Start Date' },
                                { fromKey: 'endDateFrom' as const, toKey: 'endDateTo' as const, label: 'End Date' },
                            ].map(({ fromKey, toKey, label }) => (
                                <div key={fromKey}>
                                    <div style={{ fontSize: 12, color: '#666a8d', marginBottom: 4 }}>{label}</div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <Input type="datetime-local" value={draftFilters[fromKey]} onChange={(e) => handleDraftFilterChange(fromKey, e.target.value)} />
                                        <Input type="datetime-local" value={draftFilters[toKey]} onChange={(e) => handleDraftFilterChange(toKey, e.target.value)} />
                                    </div>
                                </div>
                            ))}
                            <div>
                                <div style={{ fontSize: 12, color: '#666a8d', marginBottom: 4 }}>Duration (seconds)</div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Input type="number" min={0} placeholder="From" value={draftFilters.durationFrom} onChange={(e) => handleDraftFilterChange('durationFrom', e.target.value)} />
                                    <Input type="number" min={0} placeholder="To" value={draftFilters.durationTo} onChange={(e) => handleDraftFilterChange('durationTo', e.target.value)} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                                <Button type="link" onClick={handleClearAllFilters}>
                                    Clear all
                                </Button>
                                <Button type="primary" onClick={handleApplyAdvancedFilters}>
                                    Apply
                                </Button>
                            </div>
                        </div>
                    }
                >
                    <Button>
                        <BsPlusLg style={{ marginRight: 6 }} />
                        {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
                    </Button>
                </Popover>
            </div>

            <RunHistoryTable
                runs={runs}
                isLoading={isLoading}
                onRunClick={handleRunClick}
                onMarkSuccess={(run) => setConfirmAction({ type: 'success', run })}
                onMarkFailed={(run) => setConfirmAction({ type: 'failed', run })}
                onDeleteRequest={(run) => setConfirmAction({ type: 'delete', run })}
            />

            <div className={styles.paginationRow} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#666a8d', fontSize: 13 }}>{`Showing ${runs.length} of ${totalEntries} runs`}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        Prev
                    </Button>
                    <span style={{ color: '#666a8d', fontSize: 13 }}>{`Page ${page} of ${totalPages}`}</span>
                    <Button size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </div>
            </div>

            {confirmAction && (
                <Modal
                    visible
                    className="airflowDashboardRoot"
                    title={CONFIRM_COPY[confirmAction.type].title}
                    onCancel={() => setConfirmAction(null)}
                    onOk={handleConfirmAction}
                    okText="Confirm"
                    okButtonProps={{
                        style: {
                            backgroundColor: CONFIRM_COPY[confirmAction.type].okColor,
                            borderColor: CONFIRM_COPY[confirmAction.type].okColor,
                            boxShadow: `0 1px 4px 0 ${CONFIRM_COPY[confirmAction.type].okGlow}`,
                        },
                    }}
                >
                    {CONFIRM_COPY[confirmAction.type].description(confirmAction.run.dag_run_id)}
                </Modal>
            )}
        </div>
    );
}
